/**
 * ShopDataGate — blocks shop-user pages until shop config and shop details
 * for the current `shopId` are available.
 *
 * Behavior:
 *  - Management-role users pass through unchanged.
 *  - Shop-scoped roles (per `isShopUser`) see:
 *      * a full-screen spinner while `shopId` is not yet set (rare, transient)
 *        or while either resource is loading with no cached data;
 *      * the wrapped `children` once both `config` and `shopDetails` are
 *        available (even if a background refresh is in flight);
 *      * an error state with a Retry button (page reload) when a fetch
 *        errored and no cached data is available to fall back on.
 *
 * Fetch dispatch is delegated to `useShopConfig`, which is the single fetch
 * entry point for shop config / shop details across the app.
 */

import { FC, ReactNode } from "react";
import { useSelector } from "react-redux";

import FullScreenSpinner from "../FullScreenSpinner/FullScreenSpinner";
import useShopConfig from "../../hooks/useShopConfig";
import { selectShopId } from "../../store/allOrders.selectors";
import { isShopUser } from "../../utils/roles";
import { getSession } from "../../utils/session";

interface ShopDataGateProps {
  children: ReactNode;
}

const ShopDataGate: FC<ShopDataGateProps> = ({ children }) => {
  const role = getSession("role");
  const shopId = useSelector(selectShopId);

  const { config, shopDetails, loading, error } = useShopConfig(shopId);

  // Non-shop users bypass the gate entirely — they never consume these
  // resources.
  if (!isShopUser(role)) {
    return <>{children}</>;
  }

  // shopId is normally set synchronously by AppRouter before navigation.
  // Guard for the transient window where it isn't yet.
  if (!shopId) {
    return <FullScreenSpinner />;
  }

  // Both resources ready — render the app.
  if (config && shopDetails) {
    return <>{children}</>;
  }

  // Errored with nothing cached — offer a reload.
  if (error) {
    return (
      <FullScreenSpinner
        message="Couldn't load shop data"
        showRetry
        retryPrompt="Please retry"
        retryLabel="Reload"
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Still loading (data not present yet).
  if (loading) {
    return <FullScreenSpinner />;
  }

  // Fallback: no data, no loading, no error — behave as loading so a manual
  // navigation into a shop-scoped route without a fetch cycle doesn't render
  // half-configured children.
  return <FullScreenSpinner />;
};

export default ShopDataGate;
